// ─────────────────────────────────────────────
//  Navbar.jsx  —  Navbar UI
//  All constants are in NavbarData.js
// ─────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import "./Navbar.css";
import logoImg from "../../../assets/PNG/Logo.png";
import AdminNotificationBell from "../../Admin-TVC/AdminNotificationBell";
import AdminMobileNav from "../../Admin-TVC/AdminMobileNav";
import { useEsim, useEsimWallet } from "../../Pages/Products/Esim/Edata";
import {
  IconWA,
  IconProfile,
  IconCalendar,
  IconPhone,
  IconPlane,
  IconTools,
  IconChevron,
} from "./NavbarData.js";
import { useNavbarData } from "../../../hooks/useNavbarData";
import { resolveIcon } from "../Footer/FooterData";
import { useAuth } from "../../../context/AuthContext";
import Login from "../../Pages/Users/Login.jsx";

const EsimProfileBtn = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = open && !user ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open, user]);

  useEffect(() => {
    if (!user || !open) return;
    const fn = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target) && 
          dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [user, open]);

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  if (!user) {
    return (
      <>
        <button
          className="nav__nav__esim-user"
          title="Profile"
          aria-label="Profile"
          onClick={() => setOpen(true)}
        >
          <IconProfile />
        </button>
        {open && createPortal(
          <div className="nav__profile-overlay" onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
            <div className="nav__profile-overlay__inner" onClick={(e) => e.stopPropagation()}>
              <button className="nav__profile-overlay__close" onClick={() => setOpen(false)}>✕</button>
              <Login onLoginSuccess={(user) => { setOpen(false); navigate("/profile"); }} />
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  return (
    <div className="nav__nav__profile-wrap" ref={btnRef}>
      <button
        className="nav__nav__esim-user"
        title="Profile"
        aria-label="Profile"
        onClick={() => setOpen(o => !o)}
      >
        <IconProfile />
      </button>
      {open && (
        <div className="nav__nav__profile-dropdown" ref={dropdownRef}>
          <div className="nav__nav__profile-info">
            <span className="nav__nav__profile-email">{user.email}</span>
          </div>
          <Link to="/profile" className="nav__nav__profile-link" onClick={() => setOpen(false)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            My Profile
          </Link>
          <button className="nav__nav__profile-signout" onClick={handleLogout}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   HolidayTooltip
───────────────────────────────────────────── */
function HolidayTooltip({ holidays, anchorRect, onClose, onMouseEnter, onMouseLeave, religionColors: rc, tooltipW: tw, tooltipH: th, gap: gp }) {
  const ref = useRef(null);
  const relColors = rc || { Hindu: "#f97316", Muslim: "#00897b", Christian: "#1565c0", Sikh: "#c49000", Buddhist: "#6a1b9a", Jewish: "#2e7d32", Jain: "#c62828", National: "#546e7a", LongWeekend: "#4a5ae8", Global: "#ddd831" };
  const TW = tw || 240;
  const TH = th || 180;
  const GP = gp || 12;

  useEffect(() => {
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [onClose]);

  if (!holidays.length || !anchorRect) return null;

  const h   = holidays[0];
  const col = relColors[h.type] || "#888";

  const isMobile  = window.innerWidth <= 991;

  let style = {};

  if (isMobile) {
    const top = anchorRect.top >= TH + GP
      ? anchorRect.top - TH - GP
      : anchorRect.bottom + GP;
    style = {
      position: "fixed",
      top,
      left: "50%",
      transform: "translateX(-50%)",
      width: `min(${TW}px, calc(100vw - 2rem))`,
      zIndex: 9999999,
      pointerEvents: "auto",
    };
  } else {
    let left = anchorRect.right + GP;
    let top  = anchorRect.top;

    if (left + TW > window.innerWidth - 8) left = anchorRect.left - TW - GP;
    if (left < 8)                                  left = 8;
    if (top + TH > window.innerHeight - 8)  top  = window.innerHeight - TH - 8;
    if (top < 8)                                   top  = 8;

    style = {
      position: "fixed",
      top,
      left,
      width: TW,
      zIndex: 9999999,
      pointerEvents: "auto",
    };
  }

  return createPortal(
    <div
      className="nav__hol-tooltip"
      style={style}
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <span
        className="nav__hol-tooltip__badge"
        style={{ "--hol-bg": col + "22", "--hol-fg": col }}
      >
        {h.type}
      </span>
      <p className="nav__hol-tooltip__names">
        {holidays.map(x => x.name).join(" · ")}
      </p>
      <p className="nav__hol-tooltip__desc">{h.desc}</p>
      {h._lw && (
        <div className="nav__hol-tooltip__lw">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          Long Weekend Opportunity!
        </div>
      )}
    </div>,
    document.body
  );
}
/* ─────────────────────────────────────────────
   CalendarPanel
───────────────────────────────────────────── */
function CalendarPanel({ onClose, holidays: dynHolidays, religionColors: dynRelColors, longWeekendNotes: dynLwNotes, monthNames: dynMn, dow: dynDow, minYear: dynMinY, maxYear: dynMaxY, tooltipW: dynTw, tooltipH: dynTh, gap: dynGp }) {
  const today     = new Date();
  const [month,   setMonth]   = useState(today.getMonth() + 1);
  const [year,    setYear]    = useState(today.getFullYear());
  const [tooltip, setTooltip] = useState(null);
  const [upIdx,   setUpIdx]   = useState(0);
  const panelRef  = useRef(null);
  const upHolidays = dynHolidays || [];
  const upRelColors = dynRelColors || { Hindu: "#f97316", Muslim: "#00897b", Christian: "#1565c0", Sikh: "#c49000", Buddhist: "#6a1b9a", Jewish: "#2e7d32", Jain: "#c62828", National: "#546e7a", LongWeekend: "#4a5ae8", Global: "#ddd831" };
  const upLwNotes = dynLwNotes || [];
  const upMonthNames = dynMn || ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const upDow = dynDow || ["Mo","Tu","We","Th","Fr","Sa","Su"];
  const upMinYear = dynMinY || 2000;
  const upMaxYear = dynMaxY || 2050;
  const upTw = dynTw || 240;
  const upTh = dynTh || 180;
  const upGp = dynGp || 12;

  const getHolidaysForMonthDay = (m, d) => upHolidays.filter(h => h.month === m && h.day === d);
  const getUpcomingHolidays = () => {
    const now = new Date();
    const result = [];
    for (let y = now.getFullYear(); y <= now.getFullYear() + 1; y++) {
      for (const h of upHolidays) {
        const d = new Date(y, h.month - 1, h.day);
        if (d > now) result.push({ ...h, year: y, date: d });
      }
    }
    result.sort((a, b) => a.date - b.date);
    return result.slice(0, 30);
  };

  const upList = useRef(getUpcomingHolidays()).current;
  const tooltipTimer = useRef(null);

  useEffect(() => {
  const el = panelRef.current;
  if (!el) return;
  const fn = () => setTooltip(null);
  el.addEventListener("scroll", fn, { passive: true });
  return () => el.removeEventListener("scroll", fn);
}, []);

  /* close on outside click */
  useEffect(() => {
    const fn = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [onClose]);

  /* navigate month */
  const prevMonth = () => {
    if (month === 1)  { setMonth(12); setYear(y => Math.max(upMinYear, y - 1)); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1);  setYear(y => Math.min(upMaxYear, y + 1)); }
    else setMonth(m => m + 1);
  };

  /* build day cells */
  const firstDow    = new Date(year, month - 1, 1).getDay();
  const offset      = firstDow === 0 ? 6 : firstDow - 1;
  const daysInMonth = new Date(year, month, 0).getDate();
  const prevMonthDays = new Date(year, month - 1, 0).getDate();
  const totalCells  = Math.ceil((offset + daysInMonth) / 7) * 7;
  
const lwPositionMap = {};
upLwNotes
  .filter(n => n.month === month && n.days)
  .forEach(n => {
    n.days.forEach((d, i) => {
      const pos = i === 0 ? "start" : i === n.days.length - 1 ? "end" : "mid";
      lwPositionMap[d] = pos;
    });
  });
upHolidays
  .filter(h => h.month === month && h.day)
  .forEach(h => {
    if (lwPositionMap[h.day]) return;
    const dow = new Date(year, month - 1, h.day).getDay();
    if (dow === 5) {
      const start = h.day, end = h.day + 2;
      for (let d = start; d <= end; d++) {
        if (d >= 1 && d <= daysInMonth && !lwPositionMap[d]) {
          lwPositionMap[d] = d === start ? "start" : d === end ? "end" : "mid";
        }
      }
    } else if (dow === 1) {
      const start = h.day - 2, end = h.day;
      for (let d = start; d <= end; d++) {
        if (d >= 1 && d <= daysInMonth && !lwPositionMap[d]) {
          lwPositionMap[d] = d === start ? "start" : d === end ? "end" : "mid";
        }
      }
    }
  });
  const typesThisMonth = [...new Set(
    upHolidays.filter(h => h.month === month).map(h => h.type)
  )];

  const upItem = upList[upIdx];

  return (
    <div className="nav__cal-panel" ref={panelRef}>

      {/* ── Upcoming banner ── */}
      <div className="nav__cal-panel__upcoming">
        <button
          className="nav__cal-panel__up-arrow"
          onClick={() => setUpIdx(i => Math.max(0, i - 1))}
          disabled={upIdx === 0}
          aria-label="Previous upcoming holiday"
        >
          ‹
        </button>
        <div className="nav__cal-panel__up-body">
          <div className="nav__cal-panel__up-label">✦ UPCOMING ✦</div>
          {upItem && (
            <>
              <div className="nav__cal-panel__up-title">
                {upItem.lw ? "Long weekend on " : "Holiday on "}
                <strong>
                  {upItem.day}&nbsp;
                  {upMonthNames[upItem.month - 1].slice(0, 3)}
                </strong>
              </div>
              <div className="nav__cal-panel__up-sub">
                {upItem.name}, {upItem.day} {upMonthNames[upItem.month - 1].slice(0, 3)}
              </div>
            </>
          )}
        </div>
        <button
          className="nav__cal-panel__up-arrow"
          onClick={() => setUpIdx(i => Math.min(upList.length - 1, i + 1))}
          disabled={upIdx >= upList.length - 1}
          aria-label="Next upcoming holiday"
        >
          ›
        </button>
      </div>

      {/* ── Month / Year navigation ── */}
      <div className="nav__cal-panel__nav">
        <button className="nav__cal-panel__nav-btn" onClick={prevMonth} aria-label="Previous month">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>

        <div className="nav__cal-panel__selects">
          <select
            className="nav__cal-panel__select"
            value={month}
            onChange={e => setMonth(+e.target.value)}
            aria-label="Select month"
          >
            {upMonthNames.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          
          <input
            className="nav__cal-panel__select nav__cal-panel__select--year"
            type="number"
            value={year}
            onChange={(e) => {
              setYear(e.target.value);
            }}
            onBlur={(e) => {
              let v = Number(e.target.value);

              if (isNaN(v)) v = new Date().getFullYear();
              if (v < upMinYear) v = upMinYear;
              if (v > upMaxYear) v = upMaxYear;

              setYear(v);
            }}
            aria-label="Enter year"
          />
        </div>

        <button className="nav__cal-panel__nav-btn" onClick={nextMonth} aria-label="Next month">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>

      {/* ── Day-of-week headers ── */}
      <div className="nav__cal-panel__dow-row">
        {upDow.map(d => (
          <div key={d} className="nav__cal-panel__dow">{d}</div>
        ))}
      </div>

      {/* ── Days grid ── */}
      <div className="nav__cal-panel__grid">
        {Array.from({ length: totalCells }, (_, i) => {
          const dayNum   = i - offset + 1;
          const isInMonth = dayNum >= 1 && dayNum <= daysInMonth;
          const displayDay = isInMonth
            ? dayNum
            : dayNum < 1
              ? prevMonthDays + dayNum
              : dayNum - daysInMonth;

          const isToday = isInMonth &&
            dayNum === today.getDate() &&
            month === today.getMonth() + 1 &&
            year  === today.getFullYear();

          const holidays = isInMonth ? getHolidaysForMonthDay(month, dayNum) : [];
          const holidaysWithLw = holidays.map(h => ({
            ...h,
            _lw: [1, 5, 6].includes(new Date(year, month - 1, h.day).getDay())
          }));
          const hasHol   = holidaysWithLw.length > 0;
          const dotColor = hasHol ? (upRelColors[holidaysWithLw[0].type] || "#888") : null;

          const lwPos = isInMonth ? lwPositionMap[dayNum] : null;

          const cls = Array.from(new Set([
            "nav__cal-panel__day",
            !isInMonth && "nav__cal-panel__day--out",
            isToday && "nav__cal-panel__day--today",
            lwPos === "start" && "nav__cal-panel__day--lw-start",
            lwPos === "mid" && "nav__cal-panel__day--lw-mid",
            lwPos === "end" && "nav__cal-panel__day--lw-end",
            !lwPos && hasHol && "nav__cal-panel__day--hol",
          ].filter(Boolean))).join(" ");

          return (
            <div
              key={i}
              className={cls}
              onMouseEnter={hasHol ? (e) => {
                clearTimeout(tooltipTimer.current);
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltip({
                  holidays: holidaysWithLw,
                  rect: { top: rect.top, right: rect.right, bottom: rect.bottom, left: rect.left }
                });
              } : undefined}
              onMouseLeave={hasHol ? () => {
                tooltipTimer.current = setTimeout(() => setTooltip(null), 120);
              } : undefined}
              onClick={hasHol ? (e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltip(prev =>
                  prev && JSON.stringify(prev.rect) === JSON.stringify({ top: rect.top, right: rect.right, bottom: rect.bottom, left: rect.left })
                    ? null
                    : { holidays: holidaysWithLw, rect: { top: rect.top, right: rect.right, bottom: rect.bottom, left: rect.left } }
                );
              } : undefined}
            >
              <span className="nav__cal-panel__day-num">{displayDay}</span>
              {hasHol && <span className="nav__cal-panel__day-dot" style={{ "--dot-bg": dotColor }} />}
            </div>
          );
        })}
      </div>

      {/* ── Long weekend note for this month ── */}
      {upLwNotes.filter(n => n.month === month).map((n, i) => (
        <div key={i} className="nav__cal-panel__lw-note">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {n.note}
        </div>
      ))}

      {/* ── Legend ── */}
      {typesThisMonth.length > 0 && (
        <div className="nav__cal-panel__legend">
          {typesThisMonth.map(t => (
            <span key={t} className="nav__cal-panel__legend-item">
              <span
                className="nav__cal-panel__legend-dot"
                style={{ "--leg-bg": upRelColors[t] || "#888" }}
              />
              {t}
            </span>
          ))}
        </div>
      )}

      {/* ── Hover tooltip ── */}
      {tooltip && (
        <HolidayTooltip
          holidays={tooltip.holidays}
          anchorRect={tooltip.rect}
          onClose={() => setTooltip(null)}
          onMouseEnter={() => clearTimeout(tooltipTimer.current)}
          onMouseLeave={() => {
            tooltipTimer.current = setTimeout(() => setTooltip(null), 120);
          }}
          religionColors={upRelColors}
          tooltipW={upTw}
          tooltipH={upTh}
          gap={upGp}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Navbar
───────────────────────────────────────────── */
export default function Navbar() {
  const [scrolled,       setScrolled]       = useState(false);
  const [mobileOpen,     setMobileOpen]     = useState(false);
  const [toolsOpen,       setToolsOpen]       = useState(false);
  const [hoveredTool,     setHoveredTool]     = useState(null);
  const [hoveredTab,      setHoveredTab]      = useState(null);
  const [calOpen,        setCalOpen]        = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [esimProfileOpen, setEsimProfileOpen] = useState(false);
  const [profileOverlayOpen, setProfileOverlayOpen] = useState(false);
  const [gifPlayed, setGifPlayed] = useState(false);
  const [gifKey, setGifKey] = useState(0);
  const [logoHovered, setLogoHovered] = useState(false);
const [activeTab, setActiveTab] = useState("explore");
const [adminUser, setAdminUser] = useState(() => {
  try { return JSON.parse(sessionStorage.getItem("tvc_user")); } catch { return null; }
});
const location = useLocation();
const { user: authUser } = useAuth();
  const isEsimPage = location.pathname === "/Esim";
  const isHomePage = location.pathname === "/" || location.pathname === "/home";
  const isAdminPage = location.pathname.startsWith("/admin") || location.pathname.startsWith("/manager") || location.pathname.startsWith("/staff");

  

  const { esimState, setEsimState } = useEsim();
  const { walletBalance } = useEsimWallet();
  const { cart, user, showAuth } = esimState;
const toolsRef = useRef(null);
const calRef  = useRef(null);
const esimProfileRef = useRef(null);
const lastScrollY = useRef(0);

/* scroll listener */
useEffect(() => {
  const fn = () => {
    const currentY = window.scrollY;
    if (currentY < lastScrollY.current) {
      setToolsOpen(false);
      setCalOpen(false);
    }
    setScrolled(currentY > 10);
    lastScrollY.current = currentY;
  };
  window.addEventListener("scroll", fn, { passive: true });
  return () => window.removeEventListener("scroll", fn);
}, []);

  /* sync admin user from sessionStorage */
  useEffect(() => {
    const fn = () => {
      try { setAdminUser(JSON.parse(sessionStorage.getItem("tvc_user"))); } catch { setAdminUser(null); }
    };
    const id = setInterval(fn, 2000);
    return () => { clearInterval(id); };
  }, []);

  /* lock body scroll when mobile drawer open */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  /* close features dropdown on outside click */
  useEffect(() => {
    const fn = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setToolsOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  /* close eSIM profile dropdown on outside click */
  useEffect(() => {
    const fn = (e) => {
      if (esimProfileRef.current && !esimProfileRef.current.contains(e.target)) setEsimProfileOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  /* gif auto-play once on load */
  useEffect(() => {
    const timer = setTimeout(() => setGifPlayed(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  const navigate    = useNavigate();
  const closeMenu   = () => setMobileOpen(false);
  const closeCalendar = useCallback(() => setCalOpen(false), []);

  const normalizeImg = (v) => v && typeof v === "object" ? v.png || "" : v || "";
  const { data: navDynamic, loading } = useNavbarData();
  const dynamicToolLinks = navDynamic?.toolLinks ?? [];
  const dynamicToolImages = {};
  const dynamicToolGifs = {};
  if (navDynamic?.toolImages) {
    for (const [key, val] of Object.entries(navDynamic.toolImages)) {
      if (typeof val === "object") {
        dynamicToolImages[key] = val.png || "";
        dynamicToolGifs[key] = val.gif || "";
      } else {
        dynamicToolImages[key] = val;
        dynamicToolGifs[key] = "";
      }
    }
  }
  const dynamicTabs = (navDynamic?.tabs ?? []).map((t) => ({
    ...t,
    png: typeof t.png === "object" ? (t.png?.png || "") : (t.png || ""),
    gif: typeof t.gif === "object" ? (t.gif?.gif || "") : (t.gif || ""),
  }));
  const dynamicMobileMenuLinks = navDynamic?.mobileMenuLinks ?? [];
  const dynamicMobileQuickActions = navDynamic?.mobileQuickActions ?? [];
  const dynamicWhatsapp = navDynamic?.whatsapp ?? {};
  const dynamicCalendar = navDynamic?.calendar ?? {};
  const dynamicBrand = {
    ...(navDynamic?.whatsapp || {}),
    ...(navDynamic?.brand || {}),
  };
  const dynamicWhatsappLink = dynamicWhatsapp.number
    ? `https://wa.me/${dynamicWhatsapp.number}?text=${encodeURIComponent(dynamicWhatsapp.pretext || "")}`
    : "#";
  const dynamicHolidays = dynamicCalendar.holidays ?? [];
  const rawReligionColors = dynamicCalendar.religionColors;
  const defaultReligionColors = { Hindu: "#f97316", Muslim: "#00897b", Christian: "#1565c0", Sikh: "#c49000", Buddhist: "#6a1b9a", Jewish: "#2e7d32", Jain: "#c62828", National: "#546e7a", LongWeekend: "#4a5ae8", Global: "#ddd831" };
  const dynamicReligionColors = Array.isArray(rawReligionColors)
    ? { ...defaultReligionColors, ...Object.fromEntries(rawReligionColors.filter((r) => r.key).map((r) => [r.key, r.value])) }
    : { ...defaultReligionColors, ...(rawReligionColors || {}) };
  const dynamicLongWeekendNotes = dynamicCalendar.longWeekendNotes ?? [];
  const dynamicMonthNames = dynamicCalendar.monthNames ?? ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const dynamicDow = dynamicCalendar.dow ?? ["Mo","Tu","We","Th","Fr","Sa","Su"];
  const dynamicMinYear = dynamicCalendar.minYear ?? 2000;
  const dynamicMaxYear = dynamicCalendar.maxYear ?? 2050;
  const dynamicTooltipW = dynamicCalendar.tooltipW ?? 240;
  const dynamicTooltipH = dynamicCalendar.tooltipH ?? 180;
  const dynamicGap = dynamicCalendar.gap ?? 12;
  const getDynamicHolidaysForMonthDay = (month, day) => dynamicHolidays.filter(h => h.month === month && h.day === day);
  const getDynamicUpcomingHolidays = () => {
    const now = new Date();
    const result = [];
    for (let y = now.getFullYear(); y <= now.getFullYear() + 1; y++) {
      for (const h of dynamicHolidays) {
        const d = new Date(y, h.month - 1, h.day);
        if (d > now) result.push({ ...h, year: y, date: d });
      }
    }
    result.sort((a, b) => a.date - b.date);
    return result.slice(0, 30);
  };

  const handleContactNav = useCallback((e) => {
    if (e) e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) {
        const nav = document.querySelector('.nav');
        const navH = nav ? nav.getBoundingClientRect().height : 0;
        const top = el.getBoundingClientRect().top + window.scrollY - navH - 16;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, 150);
  }, [navigate]);

  const handleVisaSuiteNav = useCallback((e) => {
    if (e) e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('visa-checker');
      if (el) {
        const nav = document.querySelector('.nav');
        const navH = nav ? nav.getBoundingClientRect().height : 0;
        const top = el.getBoundingClientRect().top + window.scrollY - navH - 16;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, 150);
  }, [navigate]);

  return (
    <>
      {/* ══════════════════ nav__nav BAR ══════════════════ */}
      <nav className={`nav__nav ${scrolled ? "nav__nav--scrolled" : "nav__nav--hero"}`}>
        <div className="nav__nav__inner">

          {/* Logo */}
          <Link to="/" className={`nav__logo${!gifPlayed || logoHovered ? " nav__logo--gif" : ""}`} onMouseEnter={() => { setLogoHovered(true); setGifKey(k => k + 1); }} onMouseLeave={() => setLogoHovered(false)}>
            <div className="nav__logo__img-wrap">
              <img key={`logo-gif-${gifKey}`} src={dynamicBrand.logoGif || logoImg} alt="" className="nav__logo__gif" />
              <img src={dynamicBrand.logoImage || logoImg} alt="TheVisaCo Logo" className="nav__logo__img" />
            </div>
            <div className="nav__logo__words">
              <span
                className="nav__logo__main"
                style={{ fontFamily: "'PT Serif', serif" }}
              >
                {dynamicBrand.logoMainText || "TheVisaCo"}
              </span>

              <span
                className="nav__logo__sub"
                style={{
                  fontFamily: "'PT Serif', serif",
                  color: "#06263e"
                }}
              >
                {dynamicBrand.logoSubText || "Zero Worry Visa"}
              </span>
            </div>
          </Link>

            {/* All nav__nav GIFs - show only on home page when hero (not scrolled) */}
            {isHomePage && !scrolled && (
            <div className="nav__nav__tabs-wrap">
              <div className="nav__nav__tabs">
                {/* All Tabs */}
                {dynamicTabs.map((tab, i) => {
                  const isHovered = hoveredTab === tab.id;
                  const showGif = !gifPlayed || isHovered;
                  return (
                  <button
                    key={tab.id}
                    className="nav__nav__tool-gif-btn"
                    onMouseEnter={() => { setHoveredTab(tab.id); setGifKey(k => k + 1); }}
                    onMouseLeave={() => setHoveredTab(null)}
                    onClick={() => {
                      setActiveTab(tab.id);
                      if (tab.id === "Visa Intelligence Suite") {
                        handleVisaSuiteNav();
                      } else if (tab.to) {
                        navigate(tab.to);
                      }
                    }}
                    aria-label={tab.label}
                    data-aos="fade-up"
                    data-aos-delay={i * 80}
                  >
                    <div className={`nav__nav__tool-gif-img-stack${showGif ? " nav__nav__tool-gif-img-stack--gif" : ""}`}>
                      <img key={`gif-${gifKey}-${tab.id}`} src={tab.gif} alt="" className="nav__nav__tool-gif-img nav__nav__tool-gif-img--gif" loading="eager" />
                      <img src={tab.png} alt={tab.label} className="nav__nav__tool-gif-img nav__nav__tool-gif-img--png" loading="lazy" />
                    </div>
                    <span className="nav__nav__tool-gif-label">{tab.label}</span>
                  </button>
                  );
                })}
              </div>
            </div>
            )}


          {/* Center - Search bar */}
          {!isEsimPage && !isAdminPage && (
          <div className={`nav__nav__links nav__nav__links--visible ${scrolled ? 'nav__nav__search-centered' : ''}`}>
            <div className={`nav__nav__search-wrap ${scrolled ? "nav__nav__search-wrap--visible" : ""}`}>
              <input
                type="text"
                className="nav__nav__search-input"
                placeholder="Where do you want to go?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchQuery.trim()) {
                    navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
                  }
                }}
              />
              <button className="nav__nav__search-btn" type="button" onClick={function() {
                const q = searchQuery.trim();
                if (q) {
                  navigate(`/?search=${encodeURIComponent(q)}`);
                }
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </button>
            </div>
            </div>
          )}

            {/* Right icons */}
            <div className="nav__nav__right">
              {/* eSIM Cart - show only on Esim page */}
              {isEsimPage && (
                <button
                  className="nav__nav__esim-cart"
                  title="Cart"
                  aria-label="Cart"
                  onClick={() => {
                    const navigate = (id) => {
                      const event = new CustomEvent('esimNavigate', { detail: { id } });
                      window.dispatchEvent(event);
                    };
                    navigate('cart');
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                  </svg>
                  {cart.length > 0 && <span className="nav__nav__esim-badge">{cart.length}</span>}
                </button>
              )}

              {/* eSIM User/Profile - show only on Esim page */}
              {isEsimPage && user && (
                <React.Fragment>
                  <div className="nav__nav__esim-wallet" title="Your Rewards Balance">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                    </svg>
                    <span>₹{walletBalance.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="nav__nav__esim-profile-wrap" ref={esimProfileRef}>
                    <button
                      className="nav__nav__esim-user"
                      title="Profile"
                      aria-label="Profile"
                      onClick={() => setEsimProfileOpen(o => !o)}
                    >
                      <span>{user.name}</span>
                    </button>
                  </div>
</React.Fragment>
              )}

              {/* Schedule Meeting - desktop only */}
              {!isAdminPage && (
              <div className="nav__nav__schedule-desktop">
                <Link
                  to="/schedule-meeting"
                  className="nav__nav__schedule-btn"
                  title="Schedule Meeting"
                >
                  <IconCalendar />
                  <span style={{ color: 'white' }}>Schedule Meeting</span>
                </Link>
              </div>
              )}

              {/* WhatsApp */}
              {!isAdminPage && (
              <a
                href={dynamicWhatsappLink}
                className="nav__nav__wa"
                title="Chat on WhatsApp"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'white' }}
              >
                <IconWA />
              </a>
              )}

              {/* Profile - desktop only */}
              {!isAdminPage && (
              <div className="nav__nav__profile-desktop">
                <EsimProfileBtn />
              </div>
              )}

              {/* Admin bell + hamburger - show only on admin pages */}
              {isAdminPage && adminUser && (
                <>
                  <AdminNotificationBell user={adminUser} />
                  <AdminMobileNav user={adminUser} />
                </>
              )}

              {/* Tools */}
              {!isAdminPage && scrolled && (
              <div className="nav__nav__tools-wrap" ref={toolsRef}>
                <button
                  className={`nav__nav__share nav__nav__tools-btn${toolsOpen ? " nav__nav__tools-btn--active" : ""}`}
                  title="Tools"
                  aria-label="Tools"
                  onClick={() => setToolsOpen(o => !o)}
                  aria-expanded={toolsOpen}
                  aria-haspopup="true"
                >
                  <IconTools />
                </button>
                {toolsOpen && (
                  <div className="nav__tools-dropdown">
                    {(dynamicToolLinks).map(l => {
                      const linkTo = l.to || l.href || "/";
                      const isActive = location.pathname === linkTo;
                      const action = l.actionType || "link";
                      const isHovered = hoveredTool === l.label;
                      const imgSrc = isHovered && dynamicToolGifs[l.label] ? dynamicToolGifs[l.label] : dynamicToolImages[l.label];
                      const handleMouseEnter = () => setHoveredTool(l.label);
                      const handleMouseLeave = () => setHoveredTool(null);
                      if (action === "scrollToSection") {
                        return (
                          <button
                            key={l.label}
                            className={`nav__tools-dropdown__item${isActive ? " nav__tools-dropdown__item--active" : ""}`}
                            onClick={() => { handleVisaSuiteNav(); setToolsOpen(false); }}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                          >
                            {imgSrc && (
                              <div className="nav__tools-dropdown__img-wrap">
                                <img
                                  src={imgSrc}
                                  alt={l.label}
                                  className="nav__tools-dropdown__img"
                                />
                              </div>
                            )}
                            <span className="nav__tools-dropdown__label">{l.label}</span>
                            <span className="nav__tools-dropdown__desc">{l.desc}</span>
                          </button>
                        );
                      }
                      if (action === "openCalendar") {
                        return (
                          <button
                            key={l.label}
                            className="nav__tools-dropdown__item"
                            onClick={() => { setCalOpen(true); setToolsOpen(false); }}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                          >
                            {imgSrc && (
                              <div className="nav__tools-dropdown__img-wrap">
                                <img
                                  src={imgSrc}
                                  alt={l.label}
                                  className="nav__tools-dropdown__img"
                                />
                              </div>
                            )}
                            <span className="nav__tools-dropdown__label">{l.label}</span>
                          </button>
                        );
                      }
                      return (
                        <Link
                          key={l.label}
                          to={linkTo}
                          className={`nav__tools-dropdown__item${isActive ? " nav__tools-dropdown__item--active" : ""}`}
                          onClick={() => setToolsOpen(false)}
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                        >
                          {imgSrc && (
                            <div className="nav__tools-dropdown__img-wrap">
                              <img
                                src={imgSrc}
                                alt={l.label}
                                className="nav__tools-dropdown__img"
                              />
                            </div>
                          )}
                          <span className="nav__tools-dropdown__label">{l.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
              )}

              {/* Calendar Panel */}
              {calOpen && (
                <div className="nav__nav__cal-wrap" ref={calRef}>
                  <CalendarPanel
                    onClose={closeCalendar}
                    holidays={dynamicHolidays}
                    religionColors={dynamicReligionColors}
                    longWeekendNotes={dynamicLongWeekendNotes}
                    monthNames={dynamicMonthNames}
                    dow={dynamicDow}
                    minYear={dynamicMinYear}
                    maxYear={dynamicMaxYear}
                    tooltipW={dynamicTooltipW}
                    tooltipH={dynamicTooltipH}
                    gap={dynamicGap}
                  />
                </div>
              )}

              {/* Contact Us - mobile/tablet only */}
              <div className="nav__nav__contact-mobile">
              <button
                className="nav__nav__share nav__nav__call-btn"
                title="Contact Us"
                aria-label="Contact Us"
                onClick={handleContactNav}
              >
                <IconPhone />
              </button>
              </div>

              {/* Mobile Schedule Meeting - shows only on mobile/tablet */}
              {!isAdminPage && (
              <Link
                to="/schedule-meeting"
                className="nav__nav__schedule-mobile"
                title="Schedule Meeting"
              >
                <IconCalendar />
              </Link>
              )}

              {/* Hamburger */}
              {!isAdminPage && (
              <button
                className="nav__nav__hamburger"
                onClick={() => setMobileOpen(o => !o)}
                aria-label="Toggle menu"
              >
                <span className={`nav__hamburger-icon ${mobileOpen ? "nav__is-open" : ""}`}>
                  <span /><span /><span />
                </span>
              </button>
              )}
            </div>
        </div>
      </nav>

      {/* Overlay */}
      <div
        className={`nav__mobile-overlay ${mobileOpen ? "nav__is-open" : ""}`}
        onClick={closeMenu}
      />

      {/* ══════════════════ MOBILE DRAWER ══════════════════ */}
      <div className={`nav__mobile-menu ${mobileOpen ? "nav__is-open" : ""}`}>

        {/* Header */}
        <div className="nav__mobile-menu__header">
          <Link to="/" className={`nav__mobile-menu__logo${!gifPlayed || logoHovered ? " nav__logo--gif" : ""}`} onClick={closeMenu} onMouseEnter={() => { setLogoHovered(true); setGifKey(k => k + 1); }} onMouseLeave={() => setLogoHovered(false)}>
            <div className="nav__mobile-menu__logo-img-wrap" style={{ position: "relative", flexShrink: 0 }}>
              <img key={`mobile-logo-gif-${gifKey}`} src={dynamicBrand.logoGif || logoImg} alt="" className="nav__logo__gif" />
              <img src={dynamicBrand.logoImage || logoImg} alt="TheVisaCo Logo" className="nav__logo__img" />
            </div>
            <div className="nav__logo__words">
              <span className="nav__mobile-menu__logo-text">{dynamicBrand.logoMainText || "TheVisaCo"}</span>
              <span className="nav__mobile-logo__sub">{dynamicBrand.logoSubText || "Zero Worry Visa"}</span>
            </div>
          </Link>
          <button className="nav__mobile-menu__close" onClick={closeMenu} aria-label="Close menu">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6"  y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* ── SEARCH BAR ── */}
        {!isEsimPage && !isAdminPage && (
        <div className="nav__mobile-menu__search">
          {/* <label className="nav__mobile-menu__search-label">Search destinations</label> */}
          <div className="nav__mobile-menu__search-wrap">
            <input
              type="text"
              className="nav__mobile-menu__search-input"
              placeholder="Where do you want to go?"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && searchQuery.trim()) {
                  closeMenu();
                  navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
                }
              }}
            />
            <button className="nav__mobile-menu__search-btn" type="button" onClick={function() {
              const q = searchQuery.trim();
              if (q) {
                navigate(`/?search=${encodeURIComponent(q)}`);
              }
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
          </div>
        </div>
        )}

{/* ── QUICK ACTIONS — after search ── */}
        {!isAdminPage && (
        <div className="nav__mobile-menu__quick">
          <span className="nav__mobile-menu__quick-label">Quick actions</span>
          <div className="nav__mobile-menu__quick-icons">
            {dynamicMobileQuickActions.map((action, ai) => {
              if (action.type === "whatsapp") {
                return (
                  <a
                    key={"wa" + ai}
                    href={action.href || dynamicWhatsappLink}
                    className="nav__mobile-icon-btn nav__mobile-icon-btn--labeled"
                    title="WhatsApp"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMenu}
                  >
                    {resolveIcon(action.icon)}
                  </a>
                );
              }
              if (action.type === "schedule") {
                return (
                  <Link
                    key={"qa" + ai}
                    to={action.to || "/schedule-meeting"}
                    className="nav__mobile-icon-btn nav__mobile-icon-btn--labeled"
                    title="Schedule Meeting"
                    onClick={closeMenu}
                  >
                    {resolveIcon(action.icon)}
                  </Link>
                );
              }
              if (action.type === "profile") {
                if (authUser) {
                  return (
                    <Link
                      key={"qa" + ai}
                      to="/profile"
                      className="nav__mobile-icon-btn nav__mobile-icon-btn--labeled"
                      title="Profile"
                      onClick={closeMenu}
                    >
                      {resolveIcon(action.icon)}
                    </Link>
                  );
                }
                return (
                  <button
                    key={"qa" + ai}
                    className="nav__mobile-icon-btn nav__mobile-icon-btn--labeled"
                    title="Profile"
                    onClick={() => { closeMenu(); setProfileOverlayOpen(true); }}
                  >
                    {resolveIcon(action.icon)}
                  </button>
                );
              }
              if (action.type === "calendar" || action.actionType === "openCalendar" || (action.isCustom && action.isHoliday)) {
                return (
                  <button
                    key={"qa" + ai}
                    className="nav__mobile-icon-btn nav__mobile-icon-btn--labeled"
                    title="Holiday Calendar"
                    onClick={() => {
                      closeMenu();
                      setTimeout(() => setCalOpen(true), 420);
                    }}
                  >
                    {resolveIcon(action.icon)}
                  </button>
                );
              }
              if (action.type === "contact" || action.actionType === "contactScroll" || action.isCustom) {
                return (
                  <button
                    key={"qa" + ai}
                    className="nav__mobile-icon-btn nav__mobile-icon-btn--labeled"
                    title="Contact Us"
                    onClick={() => { closeMenu(); handleContactNav(); }}
                  >
                    {resolveIcon(action.icon)}
                  </button>
                );
              }
              return null;
            })}
          </div>
        </div>
        )}

        {/* ── nav__nav LINKS ── */}
        <div className="nav__mobile-menu__links">
          {dynamicMobileMenuLinks.map((l, li) => {
            const actionType = l.actionType || (l.isCustom ? (l.isHoliday ? "openCalendar" : "scrollToSection") : "link");
            if (actionType === "openCalendar") {
              return (
                <button
                  key={"ml" + li}
                  className="nav__mobile-menu__link"
                  style={{ color: "white" }}
                  onClick={() => { closeMenu(); setTimeout(() => setCalOpen(true), 420); }}
                >
                  {l.label}
                </button>
              );
            }
            if (actionType === "scrollToSection") {
              return (
                <button
                  key={"ml" + li}
                  className="nav__mobile-menu__link"
                  style={{ color: "white" }}
                  onClick={() => { closeMenu(); handleVisaSuiteNav(); }}
                >
                  {l.label}
                </button>
              );
            }
            if (actionType === "contactScroll") {
              return (
                <button
                  key={"ml" + li}
                  className="nav__mobile-menu__link"
                  style={{ color: "white" }}
                  onClick={() => { closeMenu(); handleContactNav(); }}
                >
                  {l.label}
                </button>
              );
            }
            return (
              <Link
                key={"ml" + li}
                to={l.to || "/"}
                className="nav__mobile-menu__link"
                style={{ color: "white" }}
                onClick={closeMenu}
              >
                {l.label}
              </Link>
            );
          })}
        </div>

        {profileOverlayOpen && createPortal(
          <div className="nav__profile-overlay" onClick={(e) => { if (e.target === e.currentTarget) setProfileOverlayOpen(false); }}>
            <div className="nav__profile-overlay__inner" onClick={(e) => e.stopPropagation()}>
              <button className="nav__profile-overlay__close" onClick={() => setProfileOverlayOpen(false)}>✕</button>
              <Login onLoginSuccess={(user) => { setProfileOverlayOpen(false); navigate('/profile'); }} />
            </div>
          </div>,
          document.body
        )}

      </div>
    </>
  );
}

